import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  useMediaQuery,
  useTheme,
  Alert,
} from "@mui/material";
import { useClients } from "../../hooks/useClients";
import ClientTable from "../../components/ClientTable";
import AddClientForm from "../../components/AddClientForm";
import { usePageContext } from "../../context/PageContext";
import { fetchData } from "../../api/client";
import { useTranslation } from "react-i18next";
import DuplicateClientDialog from "../../components/clients/DuplicateClientDialog";
import MergeClientsDialog from "../../components/clients/MergeClientsDialog";

const Clients = () => {
  const { t } = useTranslation("settings");
  const { currentPage, setCurrentPage } = usePageContext();
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [notice, setNotice] = useState(null); // { message: string, severity: "success"|"info"|"warning"|"error" }
  const [isProcessing, setIsProcessing] = useState(false);
  const [dupDialog, setDupDialog] = useState({
    open: false,
    duplicates: [],
    attemptedPayload: null,
    secondaryClientId: null,
  });
  const [selectedClientIds, setSelectedClientIds] = useState([]);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    data,
    error,
    refetch,
    updateMutation,
    deleteMutation, // usado para excluir 1 (segue como estava)
    isLoading,
  } = useClients(currentPage, pageSize, search);

  // Mesmo padrão de Products: empresa principal vinda do CompanySelector/sessionStorage
  const selectedCompanyIds = useMemo(() => {
    try {
      const raw = sessionStorage.getItem("selectedCompanies");
      const arr = JSON.parse(raw || "[]");
      return Array.isArray(arr)
        ? arr.filter((n) => Number.isFinite(Number(n))).map(Number)
        : [];
    } catch {
      return [];
    }
  }, []);

  const firstProjectId = useMemo(
    () => (selectedCompanyIds.length ? selectedCompanyIds[0] : null),
    [selectedCompanyIds]
  );

  useEffect(() => {
    if (error) {
      setNotice({
        message: "Erro ao carregar clientes. Por favor, tente novamente.",
        severity: "error",
      });
    } else {
      setNotice(null);
    }
  }, [error]);

  // Busca o detalhe completo do cliente antes de abrir o form
  const openClientForEdit = async (idOrRow) => {
    try {
      setIsProcessing(true);
      setNotice(null);

      const id = typeof idOrRow === "object" ? idOrRow?.id : idOrRow;
      if (!id) {
        // novo cadastro
        setSelectedClient({});
        return;
      }

      const resp = await fetchData(`/clients/${id}`, "GET");
      const fullClient = (resp && (resp.data || resp)) || {};
      setSelectedClient(fullClient);
    } catch (e) {
      setNotice({
        message: e?.message || "Não foi possível carregar os dados completos do cliente.",
        severity: "error",
      });
      // fallback: abre com o que veio da tabela
      if (typeof idOrRow === "object") setSelectedClient(idOrRow);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleEditClient = (clientOrId) => {
    openClientForEdit(clientOrId);
  };

  const handleDeleteClient = (id) => {
    setConfirmDelete(id);
    setNotice(null);
  };

  const confirmDeleteClient = () => {
    if (!confirmDelete) return;

    setIsProcessing(true);
    deleteMutation.mutate(confirmDelete, {
      onSuccess: () => {
        setConfirmDelete(null);
        refetch();
      },
      onError: () => {
        setNotice({
          message: "Erro ao excluir o cliente. Por favor, tente novamente.",
          severity: "error",
        });
      },
      onSettled: () => {
        setIsProcessing(false);
      },
    });
  };

  // <<< NOVO: exclusão em massa >>>
  const handleDeleteSelected = async (ids) => {
    if (!Array.isArray(ids) || ids.length === 0) return;

    setIsProcessing(true);
    setNotice(null);

    try {
      // Caso não exista um endpoint de bulk no backend, removemos 1 a 1.
      // Se você tiver /clients/bulk-delete, troque por uma única chamada POST/DELETE.
      const results = await Promise.allSettled(
        ids.map((id) => fetchData(`/clients/${id}`, "DELETE"))
      );

      const failures = results.filter((r) => r.status === "rejected").length;
      if (failures > 0) {
        setNotice({
          message: `Nem todos os registros foram excluídos: ${failures} falharam de ${ids.length}.`,
          severity: "warning",
        });
      }
      await refetch();
    } catch (e) {
      setNotice({
        message: e?.message || "Erro ao excluir os clientes selecionados.",
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };
  // <<< FIM NOVO >>>

  const handlePageChange = (newPage) => setCurrentPage(newPage + 1);
  const handlePageSizeChange = (newPageSize) => setPageSize(newPageSize);

  const handleSearch = (searchTerm) => {
    setSearch(typeof searchTerm === "string" ? searchTerm : "");
    setCurrentPage(1);
  };

  const handleCloseForm = () => {
    setSelectedClient(null);
    setNotice(null);
  };

  /**
   * Iniciar Conversa (nova solicitação de RELACIONAMENTO) para um cliente
   */
  const handleStartConversation = async (client) => {
    if (!client?.id) return;
    try {
      setIsProcessing(true);
      setNotice(null);

      let projectId = null;
      try {
        const raw = sessionStorage.getItem("selectedCompanies");
        const arr = JSON.parse(raw || "[]");
        if (Array.isArray(arr) && arr.length > 0) projectId = Number(arr[0]);
      } catch {
        /* ignore */
      }

      const payload = {
        projectId: projectId || undefined,
        clientId: client.id,
        client: { id: client.id },
        typeId: undefined,
        typeKind: "1", // relacionamento
        initialMessage: "",
        source: "clients-page",
      };

      const created = await fetchData("/conversations", "POST", payload);
      const conv = created?.data || created;

      if (!conv?.id) {
        throw new Error("A API não retornou o ID da solicitação criada.");
      }

      const width = window.screen.width;
      const height = window.screen.height;
      window.open(
        `/popup-call/${conv.id}`,
        "SolicitaçãoPopup",
        `width=${width},height=${height},top=0,left=0,scrollbars=no,toolbar=no,location=no,status=no,menubar=no`
      );
    } catch (e) {
      setNotice({
        message: e?.message || "Não foi possível iniciar a conversa.",
        severity: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box className="tour-clients-root" sx={{ paddingTop: 1 }}>
      {notice?.message ? (
        <Box sx={{ mb: 2 }}>
          <Alert severity={notice.severity || "info"}>{notice.message}</Alert>
        </Box>
      ) : null}

      <Box
        className="tour-clients-header"
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          mb: 2,
          gap: isMobile ? 2 : 0,
        }}
      >
        <Typography variant="h4" gutterBottom>
          {t("clientsPage.title")}
        </Typography>
        <Button
          variant="contained"
          color="primary"
          fullWidth={isMobile}
          onClick={() => openClientForEdit(null)}
          disabled={isProcessing}
        >
          {t("clientsPage.addClient")}
        </Button>
      </Box>

      {selectedClient ? (
        <AddClientForm
          client={selectedClient}
          defaultProjectId={firstProjectId}
          isFetching={isProcessing || isLoading}
          onClose={handleCloseForm}
          onDuplicateDetected={({ duplicates, attemptedPayload }) => {
            setDupDialog({
              open: true,
              duplicates,
              attemptedPayload: attemptedPayload || null,
              secondaryClientId: selectedClient?.id ? Number(selectedClient.id) : null,
            });
          }}
          onSubmit={async (updatedClient) => {
            setIsProcessing(true);
            try {
              await updateMutation.mutateAsync(updatedClient);
              await refetch();
              handleCloseForm();
            } catch (err) {
              // Mantém mensagem no contexto da página
              if (err?.code === "errors.clients.duplicate") {
                setDupDialog({
                  open: true,
                  duplicates: err?.data?.duplicates || [],
                  attemptedPayload: updatedClient || null,
                  secondaryClientId: selectedClient?.id ? Number(selectedClient.id) : null,
                });
              }
              setNotice({
                message:
                  err?.message ||
                  err?.data?.message ||
                  (err && err.response && err.response.data && err.response.data.message) ||
                  "Erro ao salvar o cliente.",
                severity: "error",
              });
              // Propaga para o formulário poder mapear para campos específicos
              throw err;
            } finally {
              setIsProcessing(false);
            }
          }}
        />
      ) : (
        <Box className="tour-clients-table">
          <ClientTable
            clients={data?.clients || []}
            rowCount={data?.pagination?.total || 0}
            pageSize={pageSize}
            page={currentPage - 1}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            onSearch={handleSearch}
            isLoading={isLoading || isProcessing}
            onEditClient={handleEditClient}
            onDeleteClient={handleDeleteClient}
            onStartConversation={handleStartConversation}
            onDeleteSelected={handleDeleteSelected}
            onSelectionChange={(ids) => setSelectedClientIds(Array.isArray(ids) ? ids : [])}
            onMergeSelected={() => setMergeDialogOpen(true)}
          />
        </Box>
      )}

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
        <DialogTitle>Confirmação de Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            {isProcessing
              ? "Processando exclusão..."
              : "Tem certeza de que deseja excluir este cliente?"}
          </Typography>
        </DialogContent>
        <DialogActions>
          {!isProcessing && (
            <>
              <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
              <Button color="error" onClick={confirmDeleteClient}>
                Excluir
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      <DuplicateClientDialog
        open={dupDialog.open}
        duplicates={dupDialog.duplicates}
        attemptedPayload={dupDialog.attemptedPayload}
        secondaryClientId={dupDialog.secondaryClientId}
        secondaryLabel={
          dupDialog.secondaryClientId ? "cliente que você estava editando" : "novo cadastro"
        }
        onChangePhone={() => {
          setDupDialog((s) => ({ ...(s || {}), open: false }));
          setNotice({ message: "Altere o telefone para continuar.", severity: "warning" });
        }}
        onClose={() => setDupDialog((s) => ({ ...(s || {}), open: false }))}
        onMerged={({ primaryId }) => {
          setDupDialog((s) => ({ ...(s || {}), open: false }));
          handleCloseForm();
          // depois de merge, volta para lista e recarrega
          refetch?.();
          // opcional: abrir cliente principal
          if (primaryId) {
            window.open(`/clients/${primaryId}`, "_blank");
          }
        }}
      />

      <MergeClientsDialog
        open={mergeDialogOpen}
        clients={(data?.clients || []).filter((c) =>
          (selectedClientIds || []).some((id) => String(id) === String(c.id))
        )}
        onClose={() => setMergeDialogOpen(false)}
        onMerged={async ({ primaryId }) => {
          setMergeDialogOpen(false);
          setSelectedClientIds([]);
          await refetch?.();
          setNotice({
            message: `Clientes unificados. Mantido o cadastro #${primaryId}.`,
            severity: "success",
          });
        }}
      />
    </Box>
  );
};

export default Clients;
